"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DodoV2Pool = exports.RState = void 0;
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const util_1 = require("../../util");
const BasePool_1 = require("./BasePool");
var RState;
(function (RState) {
    RState[RState["ONE"] = 0] = "ONE";
    RState[RState["ABOVE_ONE"] = 1] = "ABOVE_ONE";
    RState[RState["BELOW_ONE"] = 2] = "BELOW_ONE";
})(RState || (exports.RState = RState = {}));
class DodoV2Pool extends BasePool_1.BasePool {
    constructor(address, token0, token1, fee, reserve0, reserve1, state, lpFeeRate, mtFeeRate, midPrice) {
        super(address, token0, token1, fee, reserve0, reserve1);
        this.state = state;
        this.lpFeeRate = (0, util_1.getNumber)(lpFeeRate);
        this.mtFeeRate = (0, util_1.getNumber)(mtFeeRate);
        this.midPrice = (0, util_1.getNumber)(midPrice);
    }
    solveQuadraticFunctionForTrade(v0, v1, delta, i, k) {
        (0, tiny_invariant_1.default)(v0 > 0, 'DODOV2: target is zero');
        if (delta === 0)
            return 0;
        if (k === 0)
            return (i * delta) / 1e18 > v1 ? v1 : (i * delta) / 1e18;
        if (k === 1e18) {
            let temp = 0;
            const idelta = i * delta;
            if (idelta === 0)
                temp = 0;
            else if ((idelta * v1) / idelta === v1)
                temp = (idelta * v1) / (v0 * v0);
            else
                temp = (((delta * v1) / v0) * i) / v0;
            return (v1 * temp) / (temp + 1e18);
        }
        const part2 = ((k * v0) / v1) * v0 + (i * delta);
        let bAbs = (1e18 - k) * v1;
        let bSig = false;
        if (bAbs >= part2) {
            bAbs = bAbs - part2;
            bSig = false;
        }
        else {
            bAbs = part2 - bAbs;
            bSig = true;
        }
        bAbs = bAbs / 1e18;
        let squareRoot = ((1e18 - k) * 4) * ((k * v0 / 1e18) * v0) / 1e18;
        squareRoot = Math.sqrt(bAbs * bAbs + squareRoot);
        const denominator = (1e18 - k) * 2;
        let numerator = 0;
        if (bSig)
            numerator = squareRoot - bAbs;
        else
            numerator = bAbs + squareRoot;
        const v2 = numerator * 1e18 / denominator;
        if (v2 > v1)
            return 0;
        else
            return v1 - v2;
    }
    generalIntegrate(v0, v1, v2, i, k) {
        (0, tiny_invariant_1.default)(v0 > 0, 'DODOV2: target is zero');
        const fairAmount = i * (v1 - v2);
        if (k === 0)
            return fairAmount / 1e18;
        const V0V0V1V2 = ((v0 * v0 / v1) * 1e18) / v2;
        const penalty = k * V0V0V1V2 / 1e18;
        return ((1e18 - k + penalty) * fairAmount) / 1e36;
    }
    ROneSellBaseToken(st, payBaseAmount) {
        return this.solveQuadraticFunctionForTrade(st.Q0, st.Q0, payBaseAmount, st.i, st.K);
    }
    ROneSellQuoteToken(st, payQuoteAmount) {
        return this.solveQuadraticFunctionForTrade(st.B0, st.B0, payQuoteAmount, 1e36 / st.i, st.K);
    }
    RBelowSellBaseToken(st, payBaseAmount) {
        return this.solveQuadraticFunctionForTrade(st.Q0, st.Q, payBaseAmount, st.i, st.K);
    }
    RAboveSellQuoteToken(st, payQuoteAmount) {
        return this.solveQuadraticFunctionForTrade(st.B0, st.B, payQuoteAmount, 1e36 / st.i, st.K);
    }
    RAboveSellBaseToken(st, payBaseAmount) {
        return this.generalIntegrate(st.B0, st.B + payBaseAmount, st.B, st.i, st.K);
    }
    RBelowSellQuoteToken(st, payQuoteAmount) {
        return this.generalIntegrate(st.Q0, st.Q + payQuoteAmount, st.Q, 1e36 / st.i, st.K);
    }
    sellBaseToken(payBaseAmount) {
        const st = { ...this.state };
        let receiveQuoteAmount = 0;
        let newR;
        if (st.R === RState.ONE) {
            receiveQuoteAmount = this.ROneSellBaseToken(st, payBaseAmount);
            newR = RState.BELOW_ONE;
        }
        else if (st.R === RState.ABOVE_ONE) {
            const backToOnePayBase = st.B0 - st.B;
            const backToOneReceiveQuote = st.Q - st.Q0;
            if (payBaseAmount < backToOnePayBase) {
                receiveQuoteAmount = this.RAboveSellBaseToken(st, payBaseAmount);
                newR = RState.ABOVE_ONE;
                if (receiveQuoteAmount > backToOneReceiveQuote)
                    receiveQuoteAmount = backToOneReceiveQuote;
            }
            else if (payBaseAmount === backToOnePayBase) {
                receiveQuoteAmount = backToOneReceiveQuote;
                newR = RState.ONE;
            }
            else {
                receiveQuoteAmount = backToOneReceiveQuote + this.ROneSellBaseToken(st, payBaseAmount - backToOnePayBase);
                newR = RState.BELOW_ONE;
            }
        }
        else {
            receiveQuoteAmount = this.RBelowSellBaseToken(st, payBaseAmount);
            newR = RState.BELOW_ONE;
        }
        return { receiveQuoteAmount, newR };
    }
    sellQuoteToken(payQuoteAmount) {
        const st = { ...this.state };
        let receiveBaseAmount = 0;
        let newR;
        if (st.R === RState.ONE) {
            receiveBaseAmount = this.ROneSellQuoteToken(st, payQuoteAmount);
            newR = RState.ABOVE_ONE;
        }
        else if (st.R === RState.ABOVE_ONE) {
            receiveBaseAmount = this.RAboveSellQuoteToken(st, payQuoteAmount);
            newR = RState.ABOVE_ONE;
        }
        else {
            const backToOnePayQuote = st.Q0 - st.Q;
            const backToOneReceiveBase = st.B - st.B0;
            if (payQuoteAmount < backToOnePayQuote) {
                receiveBaseAmount = this.RBelowSellQuoteToken(st, payQuoteAmount);
                newR = RState.BELOW_ONE;
                if (receiveBaseAmount > backToOneReceiveBase)
                    receiveBaseAmount = backToOneReceiveBase;
            }
            else if (payQuoteAmount === backToOnePayQuote) {
                receiveBaseAmount = backToOneReceiveBase;
                newR = RState.ONE;
            }
            else {
                receiveBaseAmount = backToOneReceiveBase + this.ROneSellQuoteToken(st, payQuoteAmount - backToOnePayQuote);
                newR = RState.ABOVE_ONE;
            }
        }
        return { receiveBaseAmount, newR };
    }
    getOutput(amountIn, direction) {
        let receiveAmount = direction
            ? this.sellQuoteToken(amountIn).receiveBaseAmount
            : this.sellBaseToken(amountIn).receiveQuoteAmount;
        const mtFee = receiveAmount * this.mtFeeRate / 1e18;
        receiveAmount = receiveAmount - (receiveAmount * this.lpFeeRate / 1e18) - mtFee;
        const reserve = direction ? (0, util_1.getNumber)(this.reserve1) : (0, util_1.getNumber)(this.reserve0);
        if (receiveAmount >= reserve)
            return { output: reserve - 1, gasSpent: this.swapGasCost };
        return { output: receiveAmount, gasSpent: this.swapGasCost };
    }
    getInput(_amountOut, _direction) {
        return { input: Number.POSITIVE_INFINITY, gasSpent: this.swapGasCost };
    }
    calcCurrentPriceWithoutFee(direction) {
        return direction ? 1 / (this.midPrice / 1e18) : this.midPrice / 1e18;
    }
}
exports.DodoV2Pool = DodoV2Pool;
